import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from 'react-query';

import { getAlertGroups } from 'app/modules/alerts/core/request';
import { getAlertKeyFromType } from 'app/modules/alerts/core/util';
import { useAlertStore } from 'app/modules/alerts/store';

import { SocketClient } from 'app/hooks/socket';

import { AlertGroup, AlertsResponse, useLoadMoreAlertsProps } from '../core/type';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const useLoadMoreAlerts = ({ alertType }: useLoadMoreAlertsProps) => {
  const currentAlertGroup = useAlertStore.getState().state.alertGroup;
  const organization = useOrganizationStore((state) => state.state.organization);

  const refetchRef = useRef<() => void>(() => {});

  const queryKey = ['alerts', organization?.id, alertType];

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading, refetch } =
    useInfiniteQuery<AlertsResponse>(
      queryKey,
      async ({ pageParam = 1 }) => {
        const result = await getAlertGroups(pageParam, 10, alertType);
        return result;
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          if (!lastPage) return undefined;

          const totalFetched = allPages.reduce((acc, page) => acc + (page.items?.length || 0), 0);

          // If we've fetched fewer items than the total, get next page
          return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
        },
        enabled: !!organization?.id,
        staleTime: 30000,
        onSuccess: (alertData) => {
          if (!alertData?.pages?.length || !alertData?.pages[0]) return;

          const allAlerts = alertData.pages.flatMap((page) => page.items || []);
          const totalCount = alertData.pages[0].total || 0;

          const alertsByType: AlertGroup = {
            'Rack Failed Connection': {
              total: currentAlertGroup['Rack Failed Connection'].total,
              items: [],
            },
            'Reach Critical Level': {
              total: currentAlertGroup['Reach Critical Level'].total,
              items: [],
            },
            'Abnormal Consumption': {
              total: currentAlertGroup['Abnormal Consumption'].total,
              items: [],
            },
            'Shelf Expiry': {
              total: currentAlertGroup['Shelf Expiry'].total,
              items: [],
            },
            'Rogue Device': {
              total: currentAlertGroup['Rogue Device'].total,
              items: [],
            },
          };

          // Update the current alert type with the new items
          allAlerts.forEach((alert) => {
            const typeKey = getAlertKeyFromType(alert.type);
            alertsByType[typeKey].items.push(alert);
          });

          // // Update the total for the current alert type only
          // if (alertType) {
          //   // Get the key for the current alert type
          //   const currentTypeKey = getAlertKeyFromType(alertType);
          //   // Update the total for this specific alert type
          //   alertsByType[currentTypeKey].total = totalCount;
          // }

          useAlertStore.getState().setState({
            alertGroup: alertsByType,
            total: totalCount,
            fetched: true,
          });
        },
      },
    );

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    const handleNewAlert = (newAlertData: any) => {
      if (newAlertData) refetchRef.current();
    };

    SocketClient.onAlert(handleNewAlert);

    return () => SocketClient.offAlert();
  }, []);

  const alertItems = useMemo(() => {
    if (!data?.pages) return [];
    const allAlerts = data.pages.flatMap((page) => page.items || []);
    return allAlerts;
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (!isFetching && hasNextPage) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching]);

  return {
    isLoading: isFetching || isLoading,
    alertItems,
    hasMore: !!hasNextPage,
    handleLoadMore,
    fetched: !!data,
  };
};

export default useLoadMoreAlerts;
